import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { fetchInspectionKindList } from '#/api/imsMgmt/settings/inspectionKind/fetchList';
import { inspectionKindListQueryKey } from '#/utils/queryKeys';
import useSideDetail from '#/hooks/useSideDetail';
import { useCan } from '#/modules/infra/auth/useCan';
import SideMsg from '#/modules/commonComponents/SideMsg';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import {
  buildInspectionKindColumns,
  inspectionKindSideMsgBlocks,
} from '#/modules/imsMgmt/settings/inspectionKind/data/inspectionKindColumns';
import { getInspectionKindApiErrorMessage } from '#/modules/imsMgmt/settings/inspectionKind/helper/apiError';

function ignorePageChange() {}

const InspectionKindListPage = () => {
  const canCreate = useCan(PERM.INSPECTION_KIND_CREATE);
  const canEdit = useCan(PERM.INSPECTION_KIND_UPDATE);
  const [keyword, setKeyword] = useState('');
  const { displayData, setSelectedData } = useSideDetail();
  const listQuery = useQuery({
    queryKey: inspectionKindListQueryKey(),
    queryFn: fetchInspectionKindList,
  });

  const rows = useMemo(() => {
    const list = listQuery.data?.data ?? [];
    const query = keyword.trim().toLowerCase();
    if (!query) {
      return list;
    }
    return list.filter((row) => (
      String(row.code ?? '').toLowerCase().includes(query)
      || String(row.name ?? '').toLowerCase().includes(query)
    ));
  }, [keyword, listQuery.data]);

  const columns = useMemo(
    () => buildInspectionKindColumns({ canEdit }),
    [canEdit],
  );

  const pagination = useMemo(() => ({
    current_page: 1,
    page_size: Math.max(rows.length, 1),
    total_count: rows.length,
    total_page: 1,
  }), [rows.length]);

  function handleRowClick(row, isSelected) {
    setSelectedData(isSelected ? row.original : null);
  }

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <h1 className="page_title shrink-0">
          <span className="border-l-5 border-primary-400 pl-2.5">檢測種類</span>
        </h1>
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              tableKey="ims.inspectionKind.list"
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={listQuery.isLoading}
              isError={listQuery.isError}
              errorMessage={listQuery.isError
                ? getInspectionKindApiErrorMessage(listQuery.error, '載入列表失敗')
                : undefined}
              onRetry={listQuery.isError ? () => { listQuery.refetch(); } : undefined}
              onPageChange={ignorePageChange}
              onPageSizeChange={ignorePageChange}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={() => setKeyword('')}
              initialKeyword={keyword}
              searchPlaceholder="搜尋代碼或名稱"
              headerAction={canCreate ? <ListAddButton to={ROUTES.inspectionKindCreate} /> : null}
              onKeywordClear={() => setKeyword('')}
            />
          </div>
          <SideMsg
            title="檢測種類"
            titleValue="name"
            displayData={displayData}
            blocks={inspectionKindSideMsgBlocks}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default InspectionKindListPage;
